// ─── Domain taxonomy (mirrors backend/src/types/contextEngine.ts) ────────────

/**
 * The domain taxonomy, declared once. The runtime list and the union type come
 * from the same literal, so they cannot drift apart.
 *
 * Hand-mirrored from backend/src/types/contextEngine.ts — the two packages
 * share no build, so `npm run check:domains` compares them and fails if they
 * diverge.
 */
export const ALL_DOMAINS = [
  'mission', 'target_market', 'business_model', 'product',
  'go_to_market', 'competition', 'team', 'fundraising',
  'validation', 'brand', 'legal', 'operations',
] as const

export type ContextDomain = (typeof ALL_DOMAINS)[number]

export const DOMAIN_LABELS: Record<ContextDomain, string> = {
  mission: 'Vision & Mission',
  target_market: 'Market',
  business_model: 'Business Model',
  product: 'Product',
  go_to_market: 'Go-to-Market',
  competition: 'Competition',
  team: 'Team',
  fundraising: 'Financials',
  validation: 'Validation',
  brand: 'Brand',
  legal: 'Legal',
  operations: 'Operations',
};

// ─── Chunk model ─────────────────────────────────────────────────────────────

export interface ChunkEntity {
  name: string;
  type: 'person' | 'company' | 'product' | 'metric' | 'technology' | 'market_segment';
  salience: number;
}

export interface ContextChunkSummary {
  chunkId: string;
  domain: ContextDomain;
  subDomain?: string;
  entities: ChunkEntity[];
  sourceType: 'document' | 'audio' | 'interview' | 'artifact_feedback' | 'manual_edit' | 'inference';
  sourceId: string;
  sourceTimestamp: string;
  confidence: number;
  isContradicted: boolean;
  contradictedBy?: string;
  version: number;
  status: 'active' | 'superseded' | 'archived';
  createdAt: string;
  updatedAt: string;
  contentRef: string;
  text?: string; // Populated from GCS on detail fetch
}

// ─── Gap analysis ────────────────────────────────────────────────────────────

export interface DomainScore {
  domain: ContextDomain;
  score: number;          // 0–100
  activeChunks: number;
  avgConfidence: number;
  oldestChunkDays: number;
  gaps: string[];
}

export interface DetectedGap {
  gapId: string;
  uid: string;
  domain: ContextDomain;
  subDomain?: string;
  severity: number;       // 0.0–1.0
  description: string;
  suggestedResolution: {
    method: 'request_document' | 'ask_question' | 'infer_from_existing';
    questions?: string[];
    documentTypes?: string[];
  };
  detectedAt: string;
  resolvedAt?: string;
  status: 'open' | 'resolved' | 'dismissed';
}

export interface GapRecommendation {
  id: string;
  type: 'start_interview' | 'fill_gap' | 'refresh_domain' | 'upload_document';
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  domain?: ContextDomain;
  actionLabel: string;
  actionUrl: string;
}

export interface GapAnalysisResult {
  uid: string;
  analyzedAt: string;
  overallScore: number;
  domainScores: DomainScore[];
  gaps: DetectedGap[];
  recommendations: GapRecommendation[];
}

// ─── Context generation ──────────────────────────────────────────────────────

export interface ToolTemplate {
  templateId: string;
  toolName: string;
  category: 'code' | 'design' | 'presentation' | 'copy' | 'research' | 'documentation' | 'general';
  enabled: boolean;
  selection: {
    strategy: 'deep' | 'balanced' | 'semantic_only';
    includeDomains: ContextDomain[];
    excludeDomains: ContextDomain[];
    maxTokens: number;
  };
}

export interface FormattedContext {
  content: string;
  tokenCount: number;
  metadata: {
    templateId: string;
    strategy: string;
    chunksUsed: number;
    domainsIncluded: ContextDomain[];
    warnings: string[];
    generatedAt: string;
  };
}

// ─── Conflict model ──────────────────────────────────────────────────────────

export interface ContextConflict {
  conflictId: string;
  domain: ContextDomain;
  chunkA: ContextChunkSummary;
  chunkB: ContextChunkSummary;
  aiMergedText?: string;
  status: 'unresolved' | 'resolved';
  detectedAt: string;
  resolvedAt?: string;
  resolution?: ConflictResolutionAction;
}

export type ConflictResolutionAction = 'keep_a' | 'keep_b' | 'merge' | 'edit';

// ─── Context history ─────────────────────────────────────────────────────────

export type ContextChangeType = 'created' | 'updated' | 'archived' | 'reverted';

export interface ContextChange {
  changeId: string;
  domain: ContextDomain;
  chunkId: string;
  changeType: ContextChangeType;
  oldText?: string;
  newText?: string;
  sourceType: ContextChunkSummary['sourceType'];
  sourceId: string;
  timestamp: string;
}

export interface ContextHistoryResult {
  changes: ContextChange[];
  hasMore: boolean;
  cursor?: string;
}

// ─── Drift event (for SSE toasts) ───────────────────────────────────────────

export interface DriftEvent {
  domain: ContextDomain;
  eventType: ContextEngineEventType;
  description: string;
  chunkId?: string;
  conflictId?: string;
  timestamp: string;
}

// ─── Event types ─────────────────────────────────────────────────────────────

export type ContextEngineEventType =
  | 'CHUNKS_INDEXED'
  | 'GAP_RESOLVED'
  | 'SOURCE_OF_TRUTH_REBUILT'
  | 'GAPS_DETECTED'
  | 'CONTEXT_GENERATED'
  | 'CONFLICT_DETECTED';
